import {useEffect} from 'react';

import * as statusbar from 'components/StatusBar';
import {ItemEditorState} from 'notebook/store/VersionedItemSubstore';
import {NotePart} from 'notebook/util/NoteUtilConstants';

type useStatusBarSaveStateProps = Partial<
  Pick<
    ItemEditorState<Document, NotePart, 'document', 'documentId'>,
    'saving' | 'saveSuccess' | 'lockedFromExternalChange' | 'isAutoSaving'
  >
>;

export const UNABLE_TO_SAVE_TEXT = 'Unable to save.';
export const SYNCING_TEXT = 'Syncing...';
export const RETRYING_SYNC_TEXT = 'Retrying sync...';
export const SYNCED_TEXT = 'Synced.';

/**
 * Shows save state on the statusbar.
 *
 * @param saving Whether the document is saving.
 * @param saveSuccess Whether the document previously saved successfully.
 * @param lockedFromExternalChange Whether the document is locked from external changes.
 * @param isAutoSaving Whether auto-saving is enabled.
 */
export function useStatusBarSaveState({
  saving,
  saveSuccess,
  lockedFromExternalChange,
  isAutoSaving,
}: useStatusBarSaveStateProps): void {
  useEffect(() => {
    if (lockedFromExternalChange || !isAutoSaving) {
      statusbar.showOverlayMessage(UNABLE_TO_SAVE_TEXT);
    } else if (saving) {
      const msg = saveSuccess ? SYNCING_TEXT : RETRYING_SYNC_TEXT;
      statusbar.showOverlayMessage(msg);
    } else {
      statusbar.setTimeoutMessage(SYNCED_TEXT);
    }
  }, [isAutoSaving, lockedFromExternalChange, saveSuccess, saving]);
}
